import { Link, useNavigate, useParams } from 'react-router-dom';
import { Spinner } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { showErrorToast } from '../../../../app/components/SnackBar/SnackBar';
import { conversationPath, paths } from '../../../../app/routes/paths';
import { useRolePath } from '../../../../app/routes/useRolePath';
import { timezoneOptionLabel } from '../../../../helpers/timezone.helper';
import { useStartConversationMutation } from '../../../conversation/infrastructure/api/conversation.api';
import { formatLessonAmount } from '../../application/formatLessonPrice';
import {
  formatSlotClock,
  groupAvailableSlots,
  slotRangeIso,
} from '../../application/groupAvailableSlots';
import { useGetAvailableSlotsQuery } from '../../infrastructure/api/mentor.api';
import { useGetPublicMentorBySlugQuery } from '../../../public/infrastructure/api/public-profile.api';
import { useUserProfile } from '../../../user/ui/hooks/useUserProfile';
import { PictureByGender } from '../../../user/ui/components/PictureByGender';
import './MentorBookPage.scss';

function filled(value: string | null | undefined): string | null {
  const text = value?.trim() ?? '';
  if (!text) return null;
  return text;
}

function yearsKey(years: number, language: string): string {
  const english = !language.toLowerCase().startsWith('ua');
  if (english) return years === 1 ? 'public_years_one' : 'public_years';
  const mod10 = years % 10;
  const mod100 = years % 100;
  if (mod10 === 1 && mod100 !== 11) return 'public_years_one';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14))
    return 'public_years_few';
  return 'public_years';
}

function aboutPreview(text: string): string {
  if (text.length <= 360) return text;
  return `${text.slice(0, 360).trim()}…`;
}

export default function MentorBookPage() {
  const { t, i18n } = useTranslation();
  const { slug = '' } = useParams();
  const navigate = useNavigate();
  const to = useRolePath();
  const { profile: user } = useUserProfile();
  const { data, isLoading, isError } = useGetPublicMentorBySlugQuery(slug, {
    skip: !slug,
  });
  const profile = data?.profile;
  const range = slotRangeIso(14);
  const durationMinutes = data?.price?.durationMinutes ?? 60;
  const {
    data: slots = [],
    isLoading: slotsLoading,
    isError: slotsError,
  } = useGetAvailableSlotsQuery(
    {
      mentorId: data?.id ?? 0,
      from: range.from,
      to: range.to,
      durationMinutes,
    },
    { skip: data?.id == null },
  );
  const [startConversation, { isLoading: contacting }] =
    useStartConversationMutation();

  if (isLoading) {
    return (
      <div className="mentor-book mentor-book--state">
        <Spinner />
      </div>
    );
  }

  if (isError || !profile?.displayName) {
    return (
      <div className="mentor-book mentor-book--state">
        <p className="mentor-book__missing">{t('public-profile.not_found')}</p>
      </div>
    );
  }

  const about = filled(profile.description);
  const headline = filled(profile.headline);
  const timezone =
    timezoneOptionLabel(profile.timezone) || filled(profile.timezone);
  const languages = (profile.nativeLanguages ?? [])
    .map((code) => t(`mentor-profile.lang.${code}`))
    .filter((label) => label && !label.startsWith('mentor-profile.lang.'));
  const categories = (data.categories ?? []).filter((item) =>
    filled(item.title),
  );
  const priceLabel = data.price
    ? t('mentor-profile.price_line', {
        amount: formatLessonAmount(data.price, i18n.language),
        duration: data.price.durationMinutes,
      })
    : null;
  const years =
    profile.experienceYears != null
      ? t(`mentor-profile.${yearsKey(profile.experienceYears, i18n.language)}`, {
          years: profile.experienceYears,
        })
      : null;
  const publicPath = data.publicPath || `/public/mentor/${data.slug || slug}`;
  const groups = groupAvailableSlots(slots, i18n.language, profile.timezone);
  const studentId = user?.student?.id;
  const ownMentor = user?.mentor?.id != null && user.mentor.id === data.id;

  function onContact() {
    if (!studentId) {
      showErrorToast(t('mentorBook.needStudent'));
      return;
    }
    if (data?.id == null) return;
    startConversation({ studentId, mentorId: data.id })
      .unwrap()
      .then((conversation) => {
        navigate(to(conversationPath(conversation.id)), {
          state: {
            peerName: profile?.displayName,
            peerAvatarUrl: profile?.avatarUrl ?? null,
            mentorSlug: data.slug,
          },
        });
      })
      .catch(() => {
        showErrorToast(t('mentorBook.contactError'));
      });
  }

  return (
    <div className="mentor-book">
      <aside className="mentor-book__aside">
        <div className="mentor-book__avatar">
          <PictureByGender picture={profile.avatarUrl} gender={null} />
        </div>
        <h1 className="mentor-book__name">{profile.displayName}</h1>
        {headline ? <p className="mentor-book__headline">{headline}</p> : null}
        {years ? <p className="mentor-book__years">{years}</p> : null}
        {priceLabel ? <p className="mentor-book__price">{priceLabel}</p> : null}

        {languages.length > 0 ? (
          <div className="mentor-book__meta">
            <h2>{t('mentor-profile.steps.languages')}</h2>
            <p>{languages.join(' · ')}</p>
          </div>
        ) : null}

        {timezone ? (
          <div className="mentor-book__meta">
            <h2>{t('mentor-profile.timezone')}</h2>
            <p>{timezone}</p>
          </div>
        ) : null}

        <div className="mentor-book__actions">
          {ownMentor ? null : (
            <button
              type="button"
              className="mentor-book__contact"
              disabled={contacting}
              onClick={onContact}
            >
              {contacting ? t('mentorBook.contacting') : t('mentorBook.contact')}
            </button>
          )}
          <Link className="mentor-book__profile" to={publicPath}>
            {t('mentorBook.fullProfile')}
          </Link>
          <Link className="mentor-book__back" to={to(paths.mentors)}>
            {t('mentorBook.back')}
          </Link>
        </div>
      </aside>

      <div className="mentor-book__main">
        {about ? (
          <section className="mentor-book__section">
            <h2>{t('mentorBook.about')}</h2>
            <p>{aboutPreview(about)}</p>
          </section>
        ) : null}

        {categories.length > 0 ? (
          <section className="mentor-book__section">
            <h2>{t('mentor-profile.categories')}</h2>
            <ul className="mentor-book__chips">
              {categories.map((category) => (
                <li key={category.id}>{category.title}</li>
              ))}
            </ul>
          </section>
        ) : null}

        <section className="mentor-book__section">
          <h2>{t('mentorBook.slotsTitle')}</h2>
          {slotsLoading ? (
            <p className="mentor-book__status">{t('mentorBook.slotsLoading')}</p>
          ) : null}
          {slotsError ? (
            <p className="mentor-book__status">{t('mentorBook.slotsError')}</p>
          ) : null}
          {!slotsLoading && !slotsError && groups.length === 0 ? (
            <p className="mentor-book__status">{t('mentorBook.slotsEmpty')}</p>
          ) : null}
          {groups.map((group) => (
            <div key={group.dateKey} className="mentor-book__day">
              <h3>{group.label}</h3>
              <ul className="mentor-book__times">
                {group.slots.map((slot) => (
                  <li
                    key={slot.startsAt}
                    className="mentor-book__time"
                  >
                    {formatSlotClock(
                      slot.startsAt,
                      i18n.language,
                      profile.timezone,
                    )}
                    {' – '}
                    {formatSlotClock(
                      slot.endsAt,
                      i18n.language,
                      profile.timezone,
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </section>
      </div>
    </div>
  );
}
